import type { ComponentType, ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'

import { cartApi } from '@/api/orders'
import {
  IconBox,
  IconCart,
  IconClipboard,
  IconHome,
  IconInbox,
  IconList,
  IconPlus,
  IconRepeat,
  IconSettings,
  IconStore,
  IconTruck,
  IconUsers,
} from '@/components/icons'
import { Toaster } from '@/components/Toaster'
import { NotificationBell } from '@/features/notifications/NotificationBell'
import { useAuthStore } from '@/stores/authStore'

/**
 * Κέλυφος εφαρμογής, mobile first.
 *
 * Σε κινητό η πλοήγηση κάθεται στο κάτω μέρος της οθόνης, όπου φτάνει ο
 * αντίχειρας (§11). Από το πλάτος tablet και πάνω η ίδια πλοήγηση γίνεται
 * πλαϊνή στήλη: μια μπάρα κινητού στο κάτω μέρος μιας οθόνης 27 ιντσών
 * αφήνει το περιεχόμενο να επιπλέει μόνο του στη μέση.
 */
interface AppShellProps {
  title: string
  /** Δεύτερη γραμμή κάτω από τον τίτλο: ημερομηνία, χαιρετισμός, σύνοψη. */
  subtitle?: string
  /**
   * Η κύρια ενέργεια της οθόνης. Εμφανίζεται ως κουμπί πλήρους πλάτους στην
   * κορυφή του περιεχομένου και **όχι** στην κεφαλίδα: σε κινητό η κεφαλίδα
   * χωράει μόνο τον τίτλο και δύο εικονίδια, και ένα τέταρτο στοιχείο έσπρωχνε
   * το κουμπί εκτός οθόνης.
   */
  action?: { label: string; onClick: () => void } | null
  children: ReactNode
}

interface Tab {
  to: string
  label: string
  icon: ComponentType<{ size?: number; className?: string }>
  badge: number
  /** Μόνο στην πλαϊνή στήλη: δευτερεύουσες οθόνες που δεν χωράνε κάτω. */
  secondary?: boolean
}

export function AppShell({ title, subtitle, action, children }: AppShellProps) {
  const { t } = useTranslation()
  const session = useAuthStore((s) => s.session)

  const isSupplier = useAuthStore((s) => s.organizationType()) === 'supplier'

  // Το πλήθος του καλαθιού ενημερώνεται από τα invalidations των σελίδων, όχι
  // από polling: το WebSocket της Φάσης 6 το αναλαμβάνει πλήρως (§9).
  // Ο προμηθευτής δεν έχει καλάθι, οπότε το ερώτημα ούτε καν εκτελείται.
  const cart = useQuery({
    queryKey: ['cart'],
    queryFn: cartApi.get,
    staleTime: 60_000,
    enabled: !isSupplier,
  })

  const tabs: Tab[] = isSupplier
    ? [
        { to: '/', label: t('nav.dashboard'), icon: IconHome, badge: 0 },
        { to: '/orders', label: t('nav.incoming'), icon: IconInbox, badge: 0 },
        { to: '/supplier/catalog', label: t('nav.catalog'), icon: IconStore, badge: 0 },
        { to: '/supplier/picking-list', label: t('nav.picking'), icon: IconClipboard, badge: 0 },
        { to: '/supplier/customers', label: t('nav.customers'), icon: IconUsers, badge: 0 },
      ]
    : [
        { to: '/', label: t('nav.dashboard'), icon: IconHome, badge: 0 },
        // Η λίστα είναι η κύρια αφετηρία παραγγελίας και κάθεται δεύτερη, εκεί
        // που φτάνει πρώτα ο αντίχειρας. Οι προμηθευτές είναι διαχείριση.
        { to: '/list', label: t('nav.list'), icon: IconList, badge: 0 },
        {
          to: '/cart',
          label: t('nav.cart'),
          icon: IconCart,
          badge: cart.data?.itemCount ?? 0,
        },
        { to: '/orders', label: t('nav.orders'), icon: IconClipboard, badge: 0 },
        { to: '/suppliers', label: t('nav.suppliers'), icon: IconTruck, badge: 0 },
        // Στο κινητό η κάτω μπάρα δεν χωρά έκτο κουμπί· η σελίδα ανοίγει από
        // τη λίστα, εκεί που χρειάζεται.
        { to: '/products', label: t('nav.products'), icon: IconBox, badge: 0, secondary: true },
        { to: '/templates', label: t('nav.templates'), icon: IconRepeat, badge: 0, secondary: true },
      ]

  const organizationName =
    session?.memberships.find((m) => m.organizationId === session.organizationId)
      ?.organizationName ?? 'B2Vend'

  return (
    <div className="flex min-h-full flex-col bg-gray-50 md:[--sidebar-w:15rem]">
      {/* Η κεφαλίδα φέρει το χρώμα της μάρκας. Στο σκέτο λευκό η εφαρμογή δεν
          έχει ταυτότητα και μοιάζει με φόρμα. */}
      <header className="safe-top sticky top-0 z-10 bg-gradient-to-br from-brand-800 to-brand-600 text-white shadow-lift">
        <div className="mx-auto flex w-full max-w-3xl items-center justify-between gap-3 px-4 py-3.5 md:max-w-none md:px-6">
          <div className="min-w-0">
            <p className="truncate text-[11px] font-semibold uppercase tracking-[0.14em] text-brand-100">
              {organizationName}
            </p>
            <h1 className="truncate text-xl font-bold leading-tight">{title}</h1>
            {subtitle && <p className="truncate text-sm text-brand-100">{subtitle}</p>}
          </div>

          {/* Μόνο δύο εικονίδια: ό,τι παραπάνω δεν χωράει σε οθόνη κινητού.
              Η αποσύνδεση βρίσκεται στις Ρυθμίσεις. */}
          <div className="flex shrink-0 items-center gap-1">
            <NotificationBell />
            <NavLink
              to="/settings"
              aria-label={t('settings.title')}
              className="flex size-11 items-center justify-center rounded-xl text-white/90 transition-colors hover:bg-white/15"
            >
              <IconSettings size={21} />
            </NavLink>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Πλαϊνή πλοήγηση, μόνο σε πλατιές οθόνες. Ίδια στοιχεία με την κάτω
            μπάρα: ο χρήστης που αλλάζει συσκευή δεν ξαναμαθαίνει τίποτα. */}
        <aside className="hidden w-[var(--sidebar-w)] shrink-0 border-r border-gray-200 bg-white md:block">
          <nav className="sticky top-24 flex flex-col gap-1 p-3">
            {tabs.map((tab) => (
              <NavLink
                key={tab.to}
                to={tab.to}
                end={tab.to === '/'}
                className={({ isActive }) =>
                  'flex h-11 items-center gap-3 rounded-xl px-3 text-sm font-semibold transition-colors ' +
                  (isActive
                    ? 'bg-brand-50 text-brand-800'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900')
                }
              >
                <tab.icon size={20} className="shrink-0" />
                <span className="min-w-0 flex-1 truncate">{tab.label}</span>
                {tab.badge > 0 && (
                  <span className="inline-flex min-w-[20px] items-center justify-center rounded-full bg-accent-600 px-1.5 text-[11px] font-bold leading-5 text-white">
                    {tab.badge > 99 ? '99+' : tab.badge}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
        </aside>

        <main className="min-w-0 flex-1 px-4 py-4 pb-28 md:px-8 md:py-6 md:pb-10">
          <div className="mx-auto w-full max-w-3xl">
            {action && (
              <button
                onClick={action.onClick}
                className="mb-4 flex h-touch w-full items-center justify-center gap-2 rounded-xl bg-brand-700 px-5 text-base font-semibold text-white shadow-card transition-colors hover:bg-brand-600 active:bg-brand-900"
              >
                <IconPlus size={19} />
                {action.label}
              </button>
            )}
            <div className="rise">{children}</div>
          </div>
        </main>
      </div>

      <nav className="fixed inset-x-0 bottom-0 z-10 border-t border-gray-200 bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden">
        <div className="mx-auto flex w-full max-w-3xl">
          {tabs
            .filter((tab) => !tab.secondary)
            .map((tab) => (
              <NavLink
                key={tab.to}
                to={tab.to}
                end={tab.to === '/'}
                className={({ isActive }) =>
                  'relative flex flex-1 flex-col items-center gap-0.5 py-2 text-[11px] font-semibold transition-colors ' +
                  (isActive ? 'text-brand-700' : 'text-gray-400 hover:text-gray-600')
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={
                        'relative flex h-8 w-14 items-center justify-center rounded-full transition-colors ' +
                        (isActive ? 'bg-brand-50' : '')
                      }
                    >
                      <tab.icon size={21} />
                      {tab.badge > 0 && (
                        <span className="absolute -right-0.5 -top-0.5 inline-flex min-w-[18px] items-center justify-center rounded-full bg-accent-600 px-1 text-[10px] font-bold leading-4 text-white ring-2 ring-white">
                          {tab.badge > 99 ? '99+' : tab.badge}
                        </span>
                      )}
                    </span>
                    {tab.label}
                  </>
                )}
              </NavLink>
            ))}
        </div>
      </nav>

      <Toaster />
    </div>
  )
}

export function ConnectionBadge({ status }: { status: string | null }) {
  const { t } = useTranslation()
  if (!status) return null

  const tones: Record<string, string> = {
    active: 'bg-brand-50 text-brand-800 border-brand-200',
    pending_supplier: 'bg-amber-50 text-amber-800 border-amber-200',
    pending_customer: 'bg-amber-50 text-amber-800 border-amber-200',
    blocked: 'bg-gray-100 text-gray-600 border-gray-300',
  }

  return (
    <span
      className={`shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-medium ${tones[status] ?? tones.blocked}`}
    >
      {t(`connection.${status}`)}
    </span>
  )
}
